import { JSX, useState } from "react";
import { Badge, BadgeStyle } from "components/badges/badge/badge";
import { Button } from "components/buttons/button/button";
import { InstructorLedTrainingTypeBadge } from "components/events/instructor-led-training-type-badge/instructor-led-training-type-badge";
import { Icon } from "components/icons/icon";
import { Icons } from "components/icons/constants/icons";
import { Spinner } from "components/spinner/spinner";
import { DataTable } from "components/tables/data-table/data-table";
import { Anchor } from "components/typography/anchors/anchor/anchor";
import { useGetProviderYearEvents } from "data/data-access-hooks/events/events-queries";
import { PublishStatus } from "models/enumerations/publish-status/publish-status";
import { EventRecord } from "models/view-models/events/event-record";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useViewEventRoutes } from "utilities/hooks/models/events/use-view-event-routes";
import { RouteUtils } from "utilities/route-utils";
import { EventService } from "utilities/services/events/event-service";
import { ProviderFeeInvoiceService } from "utilities/services/providers/provider-fee-invoice-service";
import { useGetProviderFeeInvoiceList } from "data/data-access-hooks/providers/provider-fee-invoice-queries";
import { DateUtils } from "utilities/date-utils";
import { InvoiceProductTypeDisplayNames } from "models/enumerations/providers/invoice-product-type";
import { ProviderInvoiceFeeModal } from "components/user-management/providers/provider-invoice-fee-modal/provider-fee-invoice-modal";
import { ProviderFeeInvoiceRecord } from "models/view-models/providers/provider-fee-invoice-record";
import { RoleType } from "models/enumerations/users/role-type";
import { t } from "utilities/localization/t";
import "./provider-event-list.scss";

const getEventStatusBadge = (event: EventRecord): JSX.Element => {
    if (event.status === PublishStatus.Canceled) {
        return <Badge style={BadgeStyle.Error} text={t("canceled")} />;
    }
    if (event.status === PublishStatus.Live) {
        return <Badge style={BadgeStyle.Success} text={t(event.isLive() ? "live" : "completed")} />;
    }
    return <Badge style={BadgeStyle.Default} text={t("draft")} />;
};

export const ProviderEventList = ({
    providerId,
    year,
    listEvents,
    listProviderFeeInvoices,
}: {
    providerId?: number;
    year?: number;
    listEvents: ReturnType<typeof EventService.useList>["list"];
    listProviderFeeInvoices: ReturnType<typeof ProviderFeeInvoiceService.useList>["list"];
}): JSX.Element => {
    const {
        data: events,
        isLoading,
        isError,
        isEnabled,
        refetch,
    } = useGetProviderYearEvents(listEvents, providerId, year);

    const {
        data: providerFeeInvoices,
        isLoading: isLoadingProviderFeeInvoices,
        isError: isErrorProviderFeeInvoices,
        refetch: refetchProviderFeeInvoices,
    } = useGetProviderFeeInvoiceList(listProviderFeeInvoices, providerId, year);

    const [selectedProviderInvoiceFee, setSelectedProviderInvoiceFee] = useState<ProviderFeeInvoiceRecord | undefined>(undefined);
    const [isProviderInvoiceFeeModalOpen, setIsProviderInvoiceFeeModalOpen] = useState(false);

    const { record: globalState } = useGlobalState();
    const { viewEventRoutes } = useViewEventRoutes({
        roleType: globalState.currentIdentity?.role?.roleType,
    });

    const handleEditFormSave = async (): Promise<boolean> => {
        await refetchProviderFeeInvoices();
        return true;
    };

    if (!isEnabled) {
        return <p role="alert">{t("providerAndYearRequired")}</p>;
    }
    if (isLoading || isLoadingProviderFeeInvoices) {
        return (
            <div className="flex justify-center items-center py-14">
                <Spinner />
            </div>
        );
    }
    if (isError) {
        return (
            <div role="alert">
                <p>{t("thereWasAProblemReturningEvents")}</p>
                <Button
                    onClick={() => {
                        void refetch();
                    }}>
                    {t("retry")}
                </Button>
            </div>
        );
    }
    if (isErrorProviderFeeInvoices) {
        return (
            <div role="alert">
                <p>{t("thereWasAProblemReturningProviderFeeInvoices")}</p>
                <Button
                    onClick={() => {
                        void refetchProviderFeeInvoices();
                    }}>
                    {t("retry")}
                </Button>
            </div>
        );
    }
    const showEditProviderInvoiceFeeModalOnClick = (invoice?: ProviderFeeInvoiceRecord) => {
        setSelectedProviderInvoiceFee(invoice);
        setIsProviderInvoiceFeeModalOpen(true);
    };

    const isNFPAAdminOrOperator = globalState.currentIdentity?.isCurrentlyInRole(RoleType.NfpaAdministrator) ||
            globalState.currentIdentity?.isCurrentlyInRole(RoleType.NfpaOperator);

    const canEditProviderInvoiceFee = isNFPAAdminOrOperator;
    
    return (
        <>
            <DataTable cssClassName="provider-event-list">
                <thead>
                    <tr>
                        <th>{t("eventName")}</th>
                        <th>{t("id")}</th>
                        <th>{t("product")}</th>
                        <th>{t("type")}</th>
                        <th>{t("startDate")}</th>
                        <th>{t("status")}</th>
                        <th>{t("invoiced")}</th>
                        <th className="invoice-amount">{t("invoiceAmount")}</th>
                    </tr>
                </thead>
                <tbody>
                    {providerFeeInvoices?.map((invoice) => (
                        <tr key={invoice.id}>
                            <td>
                                {canEditProviderInvoiceFee && (
                                <a
                                    href="#"
                                    className="anchor"
                                    aria-haspopup="dialog"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        showEditProviderInvoiceFeeModalOnClick(invoice);
                                    }}>
                                    {invoice.invoiceTitle}
                                </a>
                                )}
                                {!canEditProviderInvoiceFee && (
                                    <span>{invoice.invoiceTitle}</span>
                                )}
                            </td>
                            <td><span className="invoiced-empty" aria-hidden="true" /></td>
                            <td>{t(InvoiceProductTypeDisplayNames[invoice.productTypeId!])}</td>
                            <td className="type">
                                <Badge style={BadgeStyle.Default} text={t("invoice")} />
                            </td>
                            <td className="start-date">
                            {DateUtils.formatDateCustom(
                                    invoice.invoiceDate?.toString(),
                                    t("shortDateFormat")
                                )} {" "}
                            </td>
                            <td className="status">
                                <Badge style={BadgeStyle.Success} text={t("completed")} />
                            </td>
                            <td className="invoiced">
                                <span className="invoiced-indicator" aria-hidden="true">
                                    <Icon type={Icons.Checkmark} />
                                </span>
                            </td>
                            <td className="invoice-amount">
                                {invoice.invoiceAmount == null || invoice.invoiceAmount === 0
                                    ? "$0"
                                    : `$${invoice.invoiceAmount.toFixed(2)}`}
                            </td>
                        </tr>
                    ))}
                    {events?.length === 0 && (
                        <tr>
                            <td colSpan={8}>{t("noEventsForThisYear")}</td>
                        </tr>
                    )}
                    {events?.map((event) => (
                        <tr key={event.id}>
                            <td>
                                <Anchor
                                    path={RouteUtils.replacePathParams(viewEventRoutes.details, {
                                        id: event.id,
                                    })}
                                    text={event.name}
                                />
                            </td>
                            <td>{event.id}</td>
                            <td>{event.product?.name}</td>
                            <td className="type">
                                {event.instructorLedTrainingType != null && (
                                    <InstructorLedTrainingTypeBadge
                                        trainingType={event.instructorLedTrainingType}
                                    />
                                )}
                            </td>
                            <td className="start-date">
                                {event.getFirstEventDateText()}{" "}
                                <Icon
                                    type={
                                        event.eventDays != null && event.eventDays.length > 1
                                            ? Icons.CalendarMulti
                                            : Icons.CalendarSingle
                                    }
                                />
                            </td>
                            <td className="status">{getEventStatusBadge(event)}</td>
                            <td className="invoiced">
                                {event.hasBeenInvoiced ? (
                                    <span className="invoiced-indicator" aria-hidden="true">
                                        <Icon type={Icons.Checkmark} />
                                    </span>
                                ) : (
                                    <span className="invoiced-empty" aria-hidden="true" />
                                )}
                                <span className="sr-only">
                                    {t(event.hasBeenInvoiced ? "invoiced" : "notInvoiced")}
                                </span>
                            </td>
                            <td className="invoice-amount">
                                {event.invoiceAmount == null || event.invoiceAmount === 0
                                    ? "$0"
                                    : `$${event.invoiceAmount.toFixed(2)}`}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </DataTable>
            {selectedProviderInvoiceFee && (
                <ProviderInvoiceFeeModal    
                    providerFeeInvoiceToEdit={selectedProviderInvoiceFee}
                    onEditFormSave={handleEditFormSave}
                    open={isProviderInvoiceFeeModalOpen}
                    setOpen={setIsProviderInvoiceFeeModalOpen}
                />
            )}
        </>
    );
};
